import { Button, Col, DatePicker, Form, FormInstance, message, Modal, Row, Select } from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import familySubscriptionApiV2 from '../../../apis/v2/FamilySubscriptionApiV2';
import userSubscriptionApiV2 from '../../../apis/v2/UserSubscriptionApiV2';
import CustomContext from '../../../context/CustomContext';
import { Group } from '../../../model/Entities';
import { FamilySubscriptionV2, UserSubscriptionV2 } from '../../../model/v2/EntitiesV2';
import notificationService from '../../../services/NotificationService';
import styles from './SubscriptionModal.module.scss';
import { subscriptionPackages } from '../../../model/v2/TypesV2';

class SubscriptionModal extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        const { user, userSubscriptions, familySubscriptions } = this.props;

        try {
            const familySubscription = familySubscriptions.find((fs) => fs.familyId === user.family?.id);
            const userSubscription = userSubscriptions.find((us) => us.userId === user.id);
            this.setState({ familySubscription });
            this.setState({ userSubscription });

            if (familySubscription?.subscriptionExpired && userSubscription?.subscriptionExpired) {
                if (familySubscription.subscriptionExpired.isAfter(userSubscription.subscriptionExpired)) {
                    this.formRef.current?.setFieldsValue(familySubscription);
                } else {
                    this.formRef.current?.setFieldsValue(userSubscription);
                }
            } else if (familySubscription?.subscriptionExpired) {
                this.formRef.current?.setFieldsValue(familySubscription);
            } else if (userSubscription?.subscriptionExpired) {
                this.formRef.current?.setFieldsValue(userSubscription);
            } else if (familySubscription) {
                this.formRef.current?.setFieldsValue(familySubscription);
            } else if (userSubscription) {
                this.formRef.current?.setFieldsValue(userSubscription);
            }
        } catch (error: any) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    save = async () => {
        const { user } = this.props;

        try {
            this.setState({ loading: 'saving' });

            const values = await this.formRef.current?.validateFields();

            if (user.family && user.family.id) {
                let familySubscription: FamilySubscriptionV2 = Object.assign({}, this.state.familySubscription, values);
                familySubscription.familyId = user.family.id;
                familySubscription.id
                    ? await familySubscriptionApiV2.update(familySubscription)
                    : await familySubscriptionApiV2.create(familySubscription);
            } else {
                let userSubscription: UserSubscriptionV2 = Object.assign({}, this.state.userSubscription, values);
                userSubscription.userId = user.id;
                userSubscription.id
                    ? await userSubscriptionApiV2.update(userSubscription)
                    : await userSubscriptionApiV2.create(userSubscription);
            }
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));

            await this.props.onUpdate();
        } catch (error: any) {
            if (!error.errorFields) {
                notificationService.displayError(error, this.props.intl);
            }
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /*** COMPONENTS ***/

    render() {
        const { loading } = this.state;

        const subscriptionPackageOptions = subscriptionPackages.map((subscriptionPackage) => (
            <Select.Option key={subscriptionPackage} value={subscriptionPackage}>
                <FormattedMessage id={subscriptionPackage} />
            </Select.Option>
        ));

        return (
            <Modal
                title={<FormattedMessage id="subscription.modal.title" />}
                className={styles.modal}
                width={600}
                visible={true}
                onCancel={this.props.onCancel}
                footer={[
                    <Button className={styles.btn} key="cancel" shape={'round'} onClick={this.props.onCancel}>
                        <FormattedMessage id="button.cancel" />
                    </Button>,
                    <Button
                        key="save"
                        shape={'round'}
                        type="primary"
                        className={styles.btn}
                        onClick={this.save}
                        loading={loading === 'saving'}
                    >
                        <FormattedMessage id="button.save" tagName="span" />
                    </Button>,
                ]}
            >
                <Form ref={this.formRef} colon={false} layout="vertical">
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<FormattedMessage id="subscription.modal.subscriptionPackage" />}
                                name="subscriptionPackage"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Select>{subscriptionPackageOptions}</Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<FormattedMessage id="subscription.modal.subscriptionExpired" />}
                                name="subscriptionExpired"
                            >
                                <DatePicker />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        );
    }
}
export default injectIntl(SubscriptionModal);

interface Props extends WrappedComponentProps {
    user: Group;
    familySubscriptions: FamilySubscriptionV2[];
    userSubscriptions: UserSubscriptionV2[];
    onUpdate: () => Promise<void>;
    onCancel: () => void;
}
interface State {
    loading?: 'loading' | 'saving';
    familySubscription?: FamilySubscriptionV2;
    userSubscription?: FamilySubscriptionV2;
}
